/**
 * Coordinate conversion between the engine's mathematical grid
 * and Phaser screen pixel positions.
 *
 * Engine grid:
 *   - Center at [0, 0]
 *   - X: -7 to +7 (left to right)
 *   - Y: -7 to +7 (engine Y — positive = down in grid rows)
 *   - Tile centers are floats: [-1.5, 0.0], [3.0, -2.5]
 *   - Normal tiles occupy 2x1 or 1x2 grid units
 *   - Doubles are perpendicular to the chain
 */

/** Pixels per engine grid unit. Adjusted based on screen size. */
let BOARD_UNIT_SIZE = 46;

/** Get current unit size */
export function getBoardUnitSize(): number {
  return BOARD_UNIT_SIZE;
}

/** Set unit size (e.g., based on screen dimensions or zoom) */
export function setBoardUnitSize(size: number): void {
  BOARD_UNIT_SIZE = size;
}

/**
 * Calculate the largest unit size that fits the board inside the given zone.
 * The board display is always 18×18 units square (14-unit grid + 2-unit frame
 * on each side).  Pass the board zone dimensions — NOT the full screen size.
 */
export function calculateBoardUnitSize(boardZoneW: number, boardZoneH: number): number {
  return Math.max(16, Math.min(
    Math.floor(boardZoneW / 18),
    Math.floor(boardZoneH / 18)
  ));
}

/**
 * Convert engine coordinates to screen pixel positions.
 *
 * @param engineX  Engine X coordinate (float, -7 to +7)
 * @param engineY  Engine Y coordinate (float, -7 to +7)
 * @param centerX  Screen pixel X of the board center
 * @param centerY  Screen pixel Y of the board center
 */
export function engineToScreen(
  engineX: number,
  engineY: number,
  centerX: number,
  centerY: number
): { x: number; y: number } {
  return {
    x: centerX + engineX * BOARD_UNIT_SIZE,
    // Engine +Y maps to screen +Y (downward)
    y: centerY + engineY * BOARD_UNIT_SIZE,
  };
}

/**
 * Convert screen pixel positions back to engine coordinates.
 */
export function screenToEngine(
  screenX: number,
  screenY: number,
  centerX: number,
  centerY: number
): { x: number; y: number } {
  return {
    x: (screenX - centerX) / BOARD_UNIT_SIZE,
    y: (screenY - centerY) / BOARD_UNIT_SIZE,
  };
}

/**
 * Get the pixel dimensions of a tile based on its orientation.
 */
export function getTileScreenDimensions(orientation: 'horizontal' | 'vertical'): {
  width: number;
  height: number;
} {
  if (orientation === 'horizontal') {
    return {
      width: BOARD_UNIT_SIZE * 2,
      height: BOARD_UNIT_SIZE * 1,
    };
  }
  return {
    width: BOARD_UNIT_SIZE * 1,
    height: BOARD_UNIT_SIZE * 2,
  };
}
